import { ChevronDown } from "lucide-react";
import { WHATSAPP_NUMBER_DISPLAY } from "../lib/whatsapp";
import { PageHero } from "../components/PageSections";
import { useSeo } from "../lib/seo";
import { ContactCards } from "../components/ContactCards";

const faqs = [
  {
    q: "How does the driving test booking support work?",
    a: "Send us your requirements and we search UK test centres for suitable earlier availability. If we find something suitable, we assist you with arranging the test date.",
  },
  {
    q: "Can you guarantee an earlier driving test date?",
    a: "No. Earlier availability depends on cancellations and DVSA releases, so an earlier date cannot be guaranteed — but we search thoroughly on your behalf.",
  },
  {
    q: "Which test centres do you cover?",
    a: "We search across UK practical driving test centres, focusing on your preferred centres and areas.",
  },
  {
    q: "What information do I need to provide for driving test support?",
    a: "Your full name, driving licence details, your existing test date if you have one, your preferred test centres or areas, preferred dates, and a contact number.",
  },
  {
    q: "What can EDT help with on an insurance claim?",
    a: "We provide motor claims support and insurance claim assistance — helping you understand the process, prepare claim forms and documentation, handle claim communication, and guiding you throughout.",
  },
  {
    q: "What details do I need for claim support?",
    a: "It helps to have your insurance or policy details, vehicle registration, incident details and date, driver details, any photos or documents, and relevant reference numbers.",
  },
  {
    q: "Can EDT help with a non-fault accident claim?",
    a: "Yes. We support both fault and non-fault motor claims, helping you understand your options, prepare the right documentation and stay informed throughout the process.",
  },
  {
    q: "Can EDT help me secure an earlier driving test?",
    a: "Yes — this is one of our core services. We search UK test centres regularly for earlier availability that matches your preferred dates and locations.",
  },
  {
    q: "How do I get in touch?",
    a: `The quickest way is WhatsApp on ${WHATSAPP_NUMBER_DISPLAY}. Send us a message telling us what you need and we will take it from there.`,
  },
  {
    q: "How quickly will I hear back?",
    a: "We aim to keep communication quick and clear. Message us on WhatsApp and we will respond as soon as we can.",
  },
];

export default function FaqContact() {
  useSeo(
    "Contact EDT Solutions | FAQ",
    "Answers to common questions about driving test support, insurance claims and bodywork repairs, plus every way to contact EDT Solutions & Services UK."
  );
  return (
    <main data-testid="faq-contact-page">
      <PageHero
        label="FAQ & Contact"
        heading="Questions, Answered"
        text="Practical answers about our driving test and claims support — and every way to reach us."
        testIdPrefix="faq"
      />

      {/* FAQ */}
      <section className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-24">
        <div className="grid items-start gap-10 lg:grid-cols-[1fr_1.4fr] lg:gap-16">
          <div>
            <p className="section-label" data-testid="faq-section-label">
              Common Questions
            </p>
            <h2
              data-testid="faq-section-heading"
              className="font-display mt-4 text-2xl leading-snug text-[#f2ede3] sm:text-[2rem]"
            >
              Frequently Asked Questions
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-[#a09a8c] sm:text-base">
              Can't find what you're looking for? Message us on WhatsApp and
              we'll be happy to help.
            </p>
          </div>

          <div className="divide-y divide-[rgba(201,162,75,0.15)] border-y border-[rgba(201,162,75,0.15)]">
            {faqs.map((faq, i) => (
              <details key={faq.q} className="group" data-testid={`faq-item-${i + 1}`}>
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-5 text-sm font-medium text-[#f2ede3] transition-colors duration-200 hover:text-[#e6c87a] [&::-webkit-details-marker]:hidden">
                  {faq.q}
                  <ChevronDown
                    size={16}
                    className="shrink-0 text-[#c9a24b] transition-transform duration-200 group-open:rotate-180"
                  />
                </summary>
                <p className="pb-5 pr-8 text-sm leading-relaxed text-[#a09a8c]">
                  {faq.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="bg-[#0e0d0c] scroll-mt-20">
        <div className="mx-auto max-w-6xl px-5 py-14 sm:px-8 sm:py-20">
          <div className="mx-auto max-w-2xl text-center">
            <div className="mx-auto h-px w-12 bg-[#c9a24b]" />
            <h2
              data-testid="contact-heading"
              className="font-display mt-5 text-2xl text-[#f2ede3] sm:text-3xl"
            >
              Contact EDT
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-[#a09a8c] sm:text-base">
              The quickest way to reach us is WhatsApp. Tell us whether you need
              driving test support or help with a claim, and include any details
              you already have.
            </p>
          </div>

          <div className="mx-auto mt-10 max-w-4xl">
            <ContactCards />
          </div>
        </div>
      </section>
    </main>
  );
}
